import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { machineFormSchema, type MachineFormData } from '@/schemas/machineSchemas';
import type { Machine } from '@/types/legacy';
import { Loader2 } from 'lucide-react';

interface MachineFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  machine?: Machine | null;
}

export function MachineFormDialog({ open, onOpenChange, machine }: MachineFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const form = useForm<MachineFormData>({
    resolver: zodResolver(machineFormSchema),
    defaultValues: {
      name: '',
      model: '',
      type: '',
      location: '',
      description: '',
      maintenance_schedule: '',
      is_active: true,
    },
  });

  // Reset form when machine changes or dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        name: machine?.name || '',
        model: machine?.model || '',
        type: machine?.type || '',
        location: machine?.location || '',
        description: machine?.description || '',
        maintenance_schedule: machine?.maintenance_schedule || '',
        is_active: machine?.is_active ?? true,
      });
    }
  }, [open, machine, form]);

  const onSubmit = async (data: MachineFormData) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        model: data.model || null,
        type: data.type || null,
        location: data.location || null,
        description: data.description || null,
        maintenance_schedule: data.maintenance_schedule || null,
        is_active: data.is_active,
      };

      if (machine) {
        const { error } = await api
          .from('machines')
          .update(payload)
          .eq('id', machine.id);

        if (error) throw error;

        toast({
          title: 'Maquina actualizada',
          description: 'La maquina se actualizo correctamente',
        });
      } else {
        const { error } = await api
          .from('machines')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Maquina creada',
          description: 'La maquina se creo correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['machines'] });
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la maquina',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{machine ? 'Editar Maquina' : 'Nueva Maquina'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Autoclave 1" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modelo</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: AC-2000" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Autoclave, Cortadora, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ubicacion</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Nave 1, Zona de corte, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripcion</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descripcion de la maquina" rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="maintenance_schedule"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Programa de Mantenimiento</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ej: Cada 3 meses" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activa</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      La maquina esta disponible para produccion
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {machine ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
